import moment from 'moment';

import { Icon } from '../../components/icon';
import { Skeleton } from '../../components/skeleton';
import { useDownloadMatterCreditStats } from '@/api/mutations/useDownloadMatterCreditStats';
import { useFetchOrgCreditByMatter } from '@/api/queries/analyticsQueries/useFetchOrgCreditByMatter';

const numberFormatter = new Intl.NumberFormat();

// Credit usage broken down by matter for a single month, rendered inside the bar-chart popover.
export function MonthCreditBreakdown({ organisationId, month }: { organisationId: string; month: string }) {
  const startDate = moment.utc(month, 'YYYY-MM').startOf('month').toDate();
  const endDate = moment.utc(month, 'YYYY-MM').endOf('month').toDate();
  const monthLabel = moment.utc(month, 'YYYY-MM').format('MMMM YYYY');

  const { data: rows, isLoading } = useFetchOrgCreditByMatter(true, organisationId, { startDate, endDate });
  const { fetchDownload, isLoading: isDownloading } = useDownloadMatterCreditStats();

  const data = rows ?? [];

  return (
    <div>
      <div className="mb-2 flex items-center justify-between gap-2 border-b border-gray-100 pb-2">
        <div>
          <p className="text-sm font-medium text-gray-900">{monthLabel}</p>
          <p className="text-xs text-gray-500">Credits by matter</p>
        </div>
        <button
          type="button"
          disabled={isDownloading || data.length === 0}
          onClick={() => fetchDownload({ organisationId, startDate, endDate })}
          className="inline-flex shrink-0 items-center gap-1 text-xs text-gray-500 hover:text-gray-800 disabled:opacity-50"
        >
          <Icon className={isDownloading ? 'lucide--loader-circle animate-spin' : 'lucide--download'} />
          Download
        </button>
      </div>

      {isLoading ? (
        <div className="flex flex-col gap-1.5">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-6 w-full bg-gray-100" />
          ))}
        </div>
      ) : data.length === 0 ? (
        <p className="py-2 text-xs text-gray-500">No credit usage this month.</p>
      ) : (
        <div className="-mx-1 flex max-h-64 flex-col overflow-y-auto">
          {data.map((row) => (
            <div key={row.matterId} className="flex items-center justify-between gap-3 rounded px-1 py-1.5">
              <span className="truncate text-sm text-gray-700">{row.matterName}</span>
              <span className="shrink-0 text-sm font-semibold text-gray-900 tabular-nums">
                {numberFormatter.format(row.credits)}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
